import styled from "@emotion/styled";
import { Button } from "react-aria-components";
import { Theme } from "@emotion/react";
import { IMDSButtonProps, MDSButtonSize, MDSButtonVariant } from "./button.types";

export const StyledButton = styled(Button)<IMDSButtonProps>(
  ({ theme, variant = MDSButtonVariant.Default, size = MDSButtonSize.Large }) => ({
    position: "relative",
    display: "flex",
    textWrap: "nowrap",
    flexDirection: "row",
    gap: theme.spacing["1"],
    justifyContent: "center",
    alignItems: "center",
    flexWrap: "nowrap",
    borderRadius: "30px",
    cursor: "pointer",
    transition: "0.25s ease",
    userSelect: "none",
    /**
     * On desktop, the outer region is draggable.
     * We want to ensure this card is not draggable.
     */
    WebkitAppRegion: "no-drag",

    fontWeight: theme.fontWeights["smedium"],
    fontSize: theme.fontSizes["text-s"],
    lineHeight: theme.lineHeights["text-s"],

    ...getSize({ size, theme }),
    ...getColors({ variant, theme }),
  })
);

const getSize = ({ size, theme }: { theme: Theme; size: MDSButtonSize }) => {
  if (size === MDSButtonSize.Small)
    return {
      padding: `6px ${theme.spacing["2"]}`,
      height: "28px",
    };
  if (size === MDSButtonSize.Medium)
    return {
      padding: `${theme.spacing["2"]} ${theme.spacing["3"]}`,
      height: "32px",
    };
  if (size === MDSButtonSize.Large)
    return {
      padding: `${theme.spacing["3"]}`,
      height: "40px",
    };
};

const getColors = ({ theme, variant }: { theme: Theme; variant: MDSButtonVariant }) => {
  const baselineStyle = {
    color: theme.colors.text.primary,
    backgroundColor: theme.colors.background.button.default.default,

    "&[data-hovered]:not([data-disabled])": {
      backgroundColor: theme.colors.background.button.default.hover,
      transition: "0.2s all",
    },

    "&[data-disabled]": {
      cursor: "not-allowed",
      opacity: 0.4,
    },

    "& .mds-icon": {
      color: theme.colors.icon.primary,
      fontSize: "12px",
      height: "12px",
      width: "12px",
    },
  };

  switch (variant) {
    case MDSButtonVariant.Default:
      return baselineStyle;

    case MDSButtonVariant.Transparent:
      return {
        ...baselineStyle,
        backgroundColor: theme.colors.background.button.transparent.default,

        "&[data-hovered]:not([data-disabled])": {
          backgroundColor: theme.colors.background.button.transparent.hover,
        },
      };

    case MDSButtonVariant.Chrome:
      return {
        ...baselineStyle,
        backgroundColor: theme.colors.background.button.chrome.default,

        "&[data-hovered]:not([data-disabled])": {
          backgroundColor: theme.colors.background.button.chrome.hover,
        },
      };

    case MDSButtonVariant.Subtle:
      return {
        ...baselineStyle,
        backgroundColor: theme.colors.background.button.subtle.default,
        color: theme.colors.text.secondary,
        opacity: 0.8,

        "&[data-hovered]:not([data-disabled])": {
          backgroundColor: theme.colors.background.button.subtle.hover,
        },

        "& .mds-icon": {
          color: theme.colors.icon.secondary,
          fontSize: "12px",
          height: "12px",
          width: "12px",
        },
      };

    case MDSButtonVariant.Contrast:
      return {
        ...baselineStyle,
        backgroundColor: theme.colors.background.button.contrast.default,
        color: theme.colors.text.onAccent,

        "&[data-hovered]:not([data-disabled])": {
          backgroundColor: theme.colors.background.button.contrast.hover,
        },

        "& .mds-icon": {
          color: theme.colors.icon.onAccent,
        },
      };
    case MDSButtonVariant.Emphasis:
      return {
        ...baselineStyle,
        backgroundColor: theme.colors.background.button.emphasis.default,
        color: theme.colors.text.onAccent,

        "&[data-hovered]:not([data-disabled])": {
          backgroundColor: theme.colors.background.button.emphasis.hover,
        },

        "& .mds-icon": {
          color: theme.colors.icon.onAccent,
        },
      };
    case MDSButtonVariant.Danger:
      return {
        ...baselineStyle,
        backgroundColor: theme.colors.background.button.danger.default,
        color: theme.colors.text.danger,

        "&[data-hovered]:not([data-disabled])": {
          backgroundColor: theme.colors.background.button.danger.hover,
        },

        "& .mds-icon": {
          color: theme.colors.icon.danger,
        },
      };

    case MDSButtonVariant.DangerFilled:
      return {
        ...baselineStyle,
        backgroundColor: theme.colors.background.button.dangerFilled.default,
        color: theme.colors.text.danger,

        "&[data-hovered]:not([data-disabled])": {
          backgroundColor: theme.colors.background.button.dangerFilled.hover,
        },

        "& .mds-icon": {
          color: theme.colors.icon.danger,
        },
      };

    case MDSButtonVariant.EmphasisLight:
      return {
        ...baselineStyle,
        backgroundColor: theme.colors.background.button.emphasisLight.default,
        color: theme.colors.text.emphasis,
        padding: `${theme.spacing["2"]} ${theme.spacing["3"]}`,

        "&[data-hovered]:not([data-disabled])": {
          backgroundColor: theme.colors.background.button.emphasisLight.hover,
        },

        "& .mds-icon": {
          color: theme.colors.icon.emphasis,
        },
      };
    case MDSButtonVariant.OnAccent:
      return {
        ...baselineStyle,
        backgroundColor: theme.colors.background.button.onAccent.default,

        "&[data-hovered]:not([data-disabled])": {
          backgroundColor: theme.colors.background.button.onAccent.hover,
        },
      };

    case MDSButtonVariant.ContrastAccent:
      return {
        ...baselineStyle,
        backgroundColor: theme.colors.background.button.contrastAccent.default,
        color: theme.colors.text.primary,

        "&[data-hovered]:not([data-disabled])": {
          backgroundColor: theme.colors.background.button.contrastAccent.hover,
        },

        "& .mds-icon": {
          color: theme.colors.icon.primary,
        },
      };
    default:
      return baselineStyle;
  }
};

export const StyledSpinnerOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: inherit;
  border-radius: inherit;
`;
